import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Checkbox,
  Radio,
  Chip,
  Alert,
  Divider,
} from "@mui/material";

export interface AdvancementTieCandidate {
  teamId: string;
  groupId: string;
  groupName: string;
  points: number;
  gamesDiff: number;
  gamesWon: number;
}

export interface AdvancementTie {
  key: string;
  bracket: "masters" | "tour";
  position: number;
  slotsAvailable: number;
  candidates: AdvancementTieCandidate[];
}

const positionLabel = (position: number) =>
  position === 0 ? "1st" : position === 1 ? "2nd" : position === 2 ? "3rd" : `${position + 1}th`;

interface AdvancementTieDialogProps {
  open: boolean;
  ties: AdvancementTie[];
  getTeamName: (teamId: string) => string;
  onCancel: () => void;
  onConfirm: (manualSelections: { [key: string]: string[] }) => void;
  submitting?: boolean;
}

export default function AdvancementTieDialog({
  open,
  ties,
  getTeamName,
  onCancel,
  onConfirm,
  submitting,
}: AdvancementTieDialogProps) {
  const [selections, setSelections] = useState<{ [key: string]: string[] }>({});

  const toggle = (tie: AdvancementTie, teamId: string) => {
    setSelections(prev => {
      const current = prev[tie.key] || [];
      if (current.includes(teamId)) {
        return { ...prev, [tie.key]: current.filter(id => id !== teamId) };
      }
      if (tie.slotsAvailable === 1) {
        return { ...prev, [tie.key]: [teamId] };
      }
      if (current.length >= tie.slotsAvailable) return prev;
      return { ...prev, [tie.key]: [...current, teamId] };
    });
  };

  const allResolved = useMemo(
    () => ties.every(tie => (selections[tie.key]?.length || 0) === tie.slotsAvailable),
    [ties, selections]
  );

  return (
    <Dialog open={open} onClose={submitting ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Resolve Tied Advancement</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          These teams are fully tied on points, game difference, and games won. Pick which
          team{ties.some(t => t.slotsAvailable > 1) ? "(s)" : ""} advance.
        </Alert>
        {ties.map((tie, idx) => {
          const chosen = selections[tie.key] || [];
          return (
            <Box key={tie.key} sx={{ mb: idx < ties.length - 1 ? 3 : 0 }}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Best {positionLabel(tie.position)} Place — pick {tie.slotsAvailable} of {tie.candidates.length}
              </Typography>
              <Paper variant="outlined">
                {tie.candidates.map((c, i) => {
                  const isChosen = chosen.includes(c.teamId);
                  return (
                    <Box key={c.teamId}>
                      {i > 0 && <Divider />}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1,
                          cursor: "pointer",
                        }}
                        onClick={() => toggle(tie, c.teamId)}
                      >
                        {tie.slotsAvailable === 1 ? (
                          <Radio checked={isChosen} size="small" />
                        ) : (
                          <Checkbox checked={isChosen} size="small" />
                        )}
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{getTeamName(c.teamId)}</Typography>
                          <Typography variant="caption" color="text.secondary">{c.groupName}</Typography>
                        </Box>
                        <Chip label={`${c.points}pts`} size="small" color="primary" sx={{ height: 20, fontSize: "0.65rem" }} />
                        <Chip label={`GD ${c.gamesDiff > 0 ? "+" : ""}${c.gamesDiff}`} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                        <Chip label={`GW ${c.gamesWon}`} size="small" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
                      </Box>
                    </Box>
                  );
                })}
              </Paper>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!allResolved || submitting}
          onClick={() => onConfirm(selections)}
        >
          {submitting ? "Generating…" : "Confirm & Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
